import React from "react";
import { isEmpty } from "lodash";
import { Row, Col } from "reactstrap";
import readXlsxFile from "read-excel-file";
import PageLayout from "../../components/PageLayout";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { drop } from "lodash";
import ValidationSummary from "../../components/ValidationSummary";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { getGridDateTemplate } from "../../utils/content";
import { getDevices, importDevices } from "../../api/devices";
import { it } from "../../utils/content";

const ImportDevicesView = () => {
  const { domains, onSuccess, onError } = useAuth0();
  const [loading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    form: {},
    errors: {},
    rows: []
  });
  const validate = formData => {
    const errors = {};
    if (
      !formData.file ||
      formData.file.type !==
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    )
      errors.file = "Selezionare un file excel";
    if (!formData.supplier) errors.supplier = `Il fornitore è obbligatorio`;
    if (!formData.insertDate)
      errors.insertDate = `La data di inserimento è obbligatoria`;
    if (!formData.type) errors.type = `Selezionare il tipo di prodotto`;
    if (!formData.rifInvoice) errors.rifInvoice = `Inserire il riferimento alla fattura`;
    if (
      !formData.lines ||
      (formData.lines &&
        formData.lines.length &&
        formData.lines.filter(row => !row[3] || !row[2]).length)
    ) {
      errors.file = `Il file selezionato non è valido: per alcuni record non sono presenti IMEI e SN`;
    }

    return errors;
  };

  const onResetForm = () => {
    setState({
      form: {},
      errors: {},
      rows: []
    });

    document.getElementById("devicesFileInput").value = "";
  };

  const onFieldChange = changedField => {
    const form = { ...state.form, ...changedField };
    const errors = validate(form);
    const rows = (form.lines || []).map(item => {
      const exist = form.existing.find(el => el.imei === item[3]);
      return exist
        ? { ...exist, existing: true }
        : {
          serial: item[2],
          imei: item[3],
          createdat: form.insertDate,
          type: form.type,
          model: item[4],
          supplier: form.supplier,
          rifInvoice: form.rifInvoice
        };
    });
    setState({ form, errors, rows });
  };

  const handleFileChange = async (e, onFieldChange) => {
    try {
      setLoading(true);
      const files = e.target.files;
      const toImport = await readXlsxFile(files[0]);
      const lines = drop(toImport, 2);
      const existing = await getDevices(lines.map(el => el[3]));
      onFieldChange({ file: files[0], lines, existing }, true);
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  };

  const supplierChange = (e, onFieldChange) => {
    onFieldChange({ supplier: e.value });
  };

  const productTypeChange = (e, onFieldChange) => {
    onFieldChange({ type: e.value });
  };

  const changeImportDate = (e, onFieldChange) => {
    onFieldChange({ insertDate: e.value });
  };

  const refInvoiceChange = (e, onFieldChange) => {
    onFieldChange({ rifInvoice: e.target.value });
  };

  const runImport = async () => {
    const devices = state.rows.filter(el => !el.existing);
    if (!devices || !devices.length) return;
    try {
      setLoading(true);
      await importDevices(devices);
      onResetForm();
      onSuccess();
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  };

  const renderRowsToImport = rows => {
    return (
      <DataTable
        value={rows}
        className="p-datatable-hoverable-rows p-datatable-customers"
        emptyMessage={() => (<div className="text-center">Nessun dato in tabella</div>)}
        rowClassName={rowData => {
          return { "p-highlight": rowData.existing };
        }}
      >
        <Column field="imei" header="IMEI" filter sortable />
        <Column field="serial" header="Seriale" filter sortable />
        <Column field="model" header="Modello" filter sortable />
        <Column field="type" header="Tipo" body={(el) => domains.devicesType.find(x => x.value === el.type).label} />
        <Column
          field="createdat"
          body={getGridDateTemplate}
          header="Creato il"
          filter
          sortable
        />
      </DataTable>
    );
  };

  const renderImportMessage = (rows) => {
    const total = rows.length;
    const existing = rows.filter(el => el.existing).length;
    const newRecords = total - existing;

    return `Il file selezionato contiene ${total} dispositivi di cui ${existing} già importati in precedenza (evidenziati in blu) e ${newRecords} nuovi.`;
  }

  return (
    <PageLayout
      title="Importazione Dispositivi"
      optionalNav={{ link: "/backoffice/devices", text: "Dispositivi" }}
    >
      <Row>
        <Col sm="6" xs="12">
          <strong className="mt-2">Fornitore</strong>
          <Dropdown
            filter
            filterBy="label"
            className={state.errors.supplier ? "p-error" : ""}
            showClear={true}
            style={{ width: "100%" }}
            value={state.form.supplier || ""}
            options={domains.suppliers}
            onChange={e => {
              supplierChange(e, onFieldChange);
            }}
          />
        </Col>
        <Col sm="6" xs="12">
          <strong className="mt-2">Tipo Prodotto</strong>
          <Dropdown
            filter
            filterBy="label"
            className={state.errors.type ? "p-error" : ""}
            showClear={true}
            style={{ width: "100%" }}
            value={state.form.type || ""}
            options={domains.devicesType}
            onChange={e => {
              productTypeChange(e, onFieldChange);
            }}
          />
        </Col>
        <Col xs="12">
          <strong className="mt-2">Riferimento Fattura</strong>
          <InputText style={{ width: "100%" }}
            value={state.form.rifInvoice || ""}
            className={state.errors.rifInvoice ? "p-error" : ""}
            onChange={e => {
              refInvoiceChange(e, onFieldChange);
            }}
          />
        </Col>
        <Col sm="6" xs="12">
          <div><strong className="mt-2">Data Inserimento</strong></div>
          <Calendar
            locale={it}
            monthNavigator={true}
            yearNavigator={true}
            showButtonBar={true}
            maxDate={new Date()}
            className={state.errors.model ? "p-error" : ""}
            yearRange="1900:2099"
            value={state.form.insertDate || ""}
            onChange={e => {
              changeImportDate(e, onFieldChange);
            }}
            dateFormat="dd/mm/yy"
            showIcon={true}
          />
        </Col>
        <Col sm="6" xs="12">
          <div><strong className="mt-2">File di import</strong></div>
          <span
            className={`p-button p-fileupload-choose p-component p-button-text-icon-left ${
              state.errors.file ? "p-error" : ""
              }`}
          >
            <span className="p-button-icon-left pi pi-plus" />
            <span className="p-button-text p-clickable">
              {state.form.file ? state.form.file.name : "Seleziona file"}
            </span>
            <input
              type="file"
              id="devicesFileInput"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "inline" }}
              onChange={e => {
                handleFileChange(e, onFieldChange);
              }}
            />
          </span>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="float-right mt-5">
            <Button
              disabled={loading || !isEmpty(state.errors)}
              className="p-button-success mr-2 mb-2"
              label="Salva"
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={runImport}
            />

            <Button
              className="p-button-warning"
              disabled={loading}
              label="Reset"
              icon="pi pi-times"
              onClick={onResetForm}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <ValidationSummary errors={state.errors} />
        </Col>
      </Row>
      {state.rows.length > 0 && (
        <Row>
          <Col>
            <strong>
              {renderImportMessage(state.rows)}
            </strong>
            {renderRowsToImport(state.rows)}
          </Col>
        </Row>
      )}
    </PageLayout>
  );
};

export default ImportDevicesView;
